import { atom } from 'infrastructure/state/jotai.wrapper'
import {
  getFilesRecursive2,
  getSheet,
  googleUserAtom,
  GDriveFile,
  GDriveFileWithPath,
} from 'infrastructure/googleDrive'
import { INode } from 'shared/types/tree'
import { gradesByIdAtom } from 'shared/api/grades'
import { getGradeId } from 'shared/utils/tree'
import {
  getFilesByUnitIdForGradeFolder,
  getFilesMappedToLessonNode,
} from './utility'
import { createIdMap } from '../../version_mapping/utility'
import { parseCsvData } from '../../Helpers'

const directoryIdAtom = atom('1KrN1fnhGpFsPPp0sNLESWLb7FEOyzNsH')
export const isUpdatingAtom = atom(false)

export const workingTreeAtom = atom<{
  tree?: INode
  resetTree?: () => void
}>({})

export type tableUpdateConfig = {
  key: string
  title: string
  attachmentsKey?: string
  filteredRows?: string[]
}

export type tableConfigMap = Record<string, tableUpdateConfig>

export const tableUpdateConfigAtom = atom<tableConfigMap>(() => {
  return {
    '[table:materials-preparation]': {
      key: 'MaterialsPreparation',
      title: 'materials preparation',
      attachmentsKey: 'materials',
      filteredRows: ['time', 'include-standard-reminder', 'lab-preparation'],
    },
    '[where-we-are-going-and-not]': {
      key: 'WhereGoing',
      title: 'where we are going and not going',
      attachmentsKey: 'going',
    },
  }
})

export const treeIdMapAtom = atom((get) => {
  const { tree } = get(workingTreeAtom)
  if (!tree) return
  return createIdMap(tree)
})

const sourceFilesAtom = atom(async (get) => {
  const guser = get(googleUserAtom)
  const dirId = get(directoryIdAtom)

  if (!guser.credential?.accessToken) return

  const files = await getFilesRecursive2(guser.credential.accessToken, dirId)
  return files.reduce<Record<string, GDriveFileWithPath[]>>((acc, file) => {
    const { name } = file
    const [gradeName] = name.split('_')
    if (!(gradeName in acc)) {
      acc[gradeName] = []
    }
    acc[gradeName].push(file)
    return acc
  }, {})
})

const filesForWorkingTreeAtom = atom(async (get) => {
  const { tree } = get(workingTreeAtom)
  if (!tree) return
  const grades = get(gradesByIdAtom)
  const gradeId = getGradeId(tree)
  if (!gradeId) return
  const gradeLevel = grades[gradeId]
  if (!gradeLevel) return
  const sourceFiles = await get(sourceFilesAtom)
  const filesForGrade = sourceFiles?.[gradeLevel.name]
  return filesForGrade?.[0]
})

export const filesMappedToLessonNodeAtom = atom(async (get) => {
  const { tree } = get(workingTreeAtom)
  if (!tree) return []
  const filesForGrade = await get(filesForWorkingTreeAtom)
  if (!filesForGrade) return []
  const files = getFilesByUnitIdForGradeFolder(filesForGrade)
  return getFilesMappedToLessonNode(tree, files)
})

export type fileLocationInfo = {
  unitNum?: number
  lessonNum?: number
  nodeId?: string
  file: GDriveFile
}

export type unitCode = { grade: string; code: string; title: string }
const oseUnitCodesAtom = atom(async (get) => {
  const guser = get(googleUserAtom)
  if (!guser.credential?.accessToken) return {}
  const doc = await getSheet(
    guser.credential.accessToken,
    '12bcGwGTrfv2RzhRXEbITTBXhceH3pzZBYS9x_YQLRus',
  )
  const result = await parseCsvData<unitCode>(doc)
  return result.reduce<Record<string, unitCode>>((acc, code) => {
    return {
      ...acc,
      [code.code.toLowerCase()]: code,
    }
  }, {})
})

export const oseMaterialCodesAtom = atom(async (get) => {
  const guser = get(googleUserAtom)
  if (!guser.credential?.accessToken) return {}
  const unitCodes = await get(oseUnitCodesAtom)
  const doc = await getSheet(
    guser.credential.accessToken,
    '1Jc2KZcL5GgHJXYamCi8FmP-bd8NO_z3ceRR76MmKr3A',
  )
  const result = await parseCsvData<{ id: string; title: string }>(doc)

  return {
    ...unitCodes,
    ...result.reduce((acc, thing) => {
      return {
        ...acc,
        [thing.id.toLowerCase()]: thing,
      }
    }, {}),
  }
})

export type videoCode = { identifier: string; url: string }

export const useVideoCodesAtom = atom(async (get) => {
  const guser = get(googleUserAtom)
  if (!guser.credential?.accessToken) return {}
  const doc = await getSheet(
    guser.credential.accessToken,
    '1_VOuBwVebqNXauAndPkHvmdmE45TfK8jNf_ngDvIivw',
  )
  return (await parseCsvData<videoCode>(doc, { startingRow: 1 })).reduce<
    Record<string, videoCode>
  >((acc, vCode) => {
    return {
      ...acc,
      [vCode.identifier.toLowerCase()]: vCode,
    }
  }, {})
})
